<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { PublicService, PublicStudio } from '@terapeut-ia/shared-types'
import AgeGate from './components/AgeGate.vue'
import ChatWidget from './components/ChatWidget.vue'
import AppointmentManagerModal from './components/AppointmentManagerModal.vue'
import BookingModal from './components/BookingModal.vue'
import ContactIcon from './components/ContactIcon.vue'
import { siteContent } from './content/site'
import { formatPublicDescription } from './lib/rich-text'
import { buildWhatsappUrl, DEFAULT_WHATSAPP_MESSAGE_TEMPLATE } from './lib/whatsapp'
import { getPublicServices, getPublicStudio } from './services/api'
import { useChatStore } from './stores/chat'

const chat = useChatStore()
const services = ref<PublicService[]>([])
const studio = ref<PublicStudio | null>(null)
const servicesUnavailable = ref(false)
const selectedService = ref<PublicService | null>(null)
const managerOpen = ref(false)
const fallbackWhatsappUrl = import.meta.env.VITE_WHATSAPP_URL ?? '#'
const genericMetadataDescription = 'Terapeut-IA® — informações, serviços e agendamento com profissionais independentes.'
const officialPublicDomain = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.terapeut-ia\.com$/i


const fallbackContent: PublicStudio['content'] = {
  displayName: siteContent.professionalName,
  heroEyebrow: siteContent.eyebrow,
  headline: siteContent.headline,
  introduction: siteContent.introduction,
  aboutTitle: 'Uma apresentação elegante, direta e acolhedora.',
  biography: siteContent.about,
  publicLocation: siteContent.publicLocation,
  servicesTitle: 'Informações e valores pela fonte operacional',
  galleryTitle: 'Galeria',
  faqTitle: 'Perguntas frequentes',
  navAboutLabel: 'Sobre', navServicesLabel: 'Serviços', navGalleryLabel: 'Galeria', navFaqLabel: 'Dúvidas', navContactsLabel: 'Contatos',
  heroPrimaryCta: 'Consultar horários', heroSecondaryCta: 'WhatsApp', heroImagePlaceholder: 'A imagem principal será escolhida no Estúdio',
  aboutLabel: 'Sobre', servicesLabel: 'Serviços', servicesManageCta: 'Gerenciar agendamento', servicesChatCta: 'Perguntar no chat', serviceCardCta: 'Consultar',
  servicesUnavailableText: 'Não consegui consultar os serviços neste momento.', servicesEmptyText: 'Os serviços serão cadastrados durante a homologação.',
  servicesEmptyChatCta: 'Continuar no chat', servicesEmptyWhatsappCta: 'Falar no WhatsApp', galleryLabel: 'Galeria', faqLabel: 'Dúvidas',
  faqItems: siteContent.faq.map((item, displayOrder) => ({ id: `fallback-${displayOrder}`, ...item, displayOrder })),
  contactsLabel: 'Contatos', contactsTitle: 'Fale comigo',
  whatsappMessageTemplate: DEFAULT_WHATSAPP_MESSAGE_TEMPLATE,
  contacts: [],
}

const publishedContent = computed(() => studio.value?.content ?? fallbackContent)
const publishedMedia = computed(() => studio.value?.media ?? [])
const heroImage = computed(() => publishedMedia.value.find((asset) => asset.mediaType === 'IMAGE'))
const visibleContacts = computed(() => publishedContent.value.contacts
  .filter((contact) => contact.isVisible)
  .sort((left, right) => left.displayOrder - right.displayOrder))
const whatsappBaseUrl = computed(() => {
  const contact = visibleContacts.value.find((item) => item.type === 'WHATSAPP')
  if (!contact) return fallbackWhatsappUrl
  return whatsappContactBaseUrl(contact.value) ?? fallbackWhatsappUrl
})

const whatsappDomain = computed(() => studio.value?.canonicalDomain?.trim() || window.location.hostname)
const whatsappUrl = computed(() => whatsappUrlWithMessage(whatsappBaseUrl.value))

function whatsappContactBaseUrl(value: string): string | null {
  if (/^https?:\/\//i.test(value)) return value
  const digits = value.replace(/\D/g, '')
  return digits ? `https://wa.me/${digits}` : null
}

function whatsappUrlWithMessage(baseUrl: string, serviceName?: string): string {
  return buildWhatsappUrl({
    baseUrl,
    messageTemplate: publishedContent.value.whatsappMessageTemplate,
    displayName: publishedContent.value.displayName,
    canonicalDomain: whatsappDomain.value,
    serviceName,
  })
}

function serviceWhatsappUrl(service: PublicService): string {
  const url = whatsappUrlWithMessage(whatsappBaseUrl.value, service.name)
  return url === '#' ? '#contatos' : url
}

function contactHref(type: string, value: string): string | null {
  if (type === 'EMAIL') return `mailto:${value}`
  if (type === 'PHONE') return `tel:${value.replace(/[^\d+]/g, '')}`
  if (type === 'WHATSAPP') {
    const baseUrl = whatsappContactBaseUrl(value)
    return baseUrl ? whatsappUrlWithMessage(baseUrl) : null
  }
  if (['INSTAGRAM', 'FACEBOOK', 'YOUTUBE', 'OTHER'].includes(type) && /^https?:\/\//i.test(value)) return value
  return null
}

function canonicalOrigin(value: string | null | undefined): string {
  const candidate = value?.trim()
  if (!candidate) return window.location.origin

  try {
    const url = new URL(candidate.includes('://') ? candidate : `https://${candidate}`)
    const hostname = url.hostname.toLowerCase()
    return officialPublicDomain.test(hostname) ? `https://${hostname}` : window.location.origin
  } catch {
    return window.location.origin
  }
}

function setMetadataContent(selector: string, content: string): void {
  document.head.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', content)
}

function updateDocumentMetadata(value: PublicStudio): void {
  const displayName = value.content.displayName.trim()
  const description = value.content.introduction.trim() || genericMetadataDescription
  const title = displayName ? `${displayName} | Terapeut-IA®` : 'Terapeut-IA®'
  const canonicalUrl = new URL(
    `${window.location.pathname}${window.location.search}`,
    canonicalOrigin(value.canonicalDomain),
  ).toString()

  document.title = title
  setMetadataContent('meta[name="description"]', description)
  setMetadataContent('meta[property="og:title"]', title)
  setMetadataContent('meta[property="og:description"]', description)
  setMetadataContent('meta[property="og:url"]', canonicalUrl)
  document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', canonicalUrl)
}

onMounted(async () => {
  const [servicesResult, studioResult] = await Promise.allSettled([
    getPublicServices(),
    getPublicStudio(),
  ])
  if (servicesResult.status === 'fulfilled') services.value = servicesResult.value
  else servicesUnavailable.value = true

  if (studioResult.status === 'fulfilled') {
    studio.value = studioResult.value
    updateDocumentMetadata(studioResult.value)
    chat.setProfessionalName(studioResult.value.content.displayName)
  } else {
    chat.setProfessionalName('')
  }
})
function openBooking(service: PublicService): void {
  selectedService.value = service
}

function handleReserved(conversationId: string): void {
  chat.adoptConversationId(conversationId)
}

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)
}
</script>

<template>
  <AgeGate>
    <div class="min-h-screen overflow-x-hidden bg-[#090507] text-white">
      <header class="fixed inset-x-0 top-0 z-40 border-b border-white/[.06] bg-[#090507]/85 backdrop-blur-xl">
        <div class="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
          <a class="font-display text-2xl tracking-wide" href="#inicio">{{ publishedContent.displayName }}</a>
          <nav class="hidden items-center gap-7 text-sm text-white/60 md:flex">
            <a href="#sobre">{{ publishedContent.navAboutLabel }}</a><a href="#servicos">{{ publishedContent.navServicesLabel }}</a><a href="#galeria">{{ publishedContent.navGalleryLabel }}</a><a href="#duvidas">{{ publishedContent.navFaqLabel }}</a><a v-if="visibleContacts.length" href="#contatos">{{ publishedContent.navContactsLabel }}</a>
          </nav>
          <div class="flex items-center gap-2">
            <div class="hidden items-center gap-1 xl:flex">
              <template v-for="contact in visibleContacts" :key="`menu-${contact.id}`">
                <a v-if="contactHref(contact.type, contact.value)" class="contact-icon-link" :href="contactHref(contact.type, contact.value) ?? '#'" target="_blank" rel="noreferrer" :aria-label="contact.label" :title="contact.label">
                  <ContactIcon :type="contact.type" />
                </a>
              </template>
              <a class="contact-icon-link" href="#responsabilidade" aria-label="Responsabilidade e privacidade" title="Responsabilidade e privacidade">
                <ContactIcon type="PRIVACY" />
              </a>
            </div>
            <button class="secondary-button hidden sm:inline-flex" type="button" @click="managerOpen = true">Meu agendamento</button>
            <button class="secondary-button" type="button" @click="chat.open('Posso explicar os serviços ou consultar horários para você.')">
              Terapeut-IA®
            </button>
          </div>
        </div>
      </header>

      <main>
        <section id="inicio" class="relative isolate min-h-screen overflow-hidden pt-20">
          <div class="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_75%_25%,rgba(139,24,55,.30),transparent_35%),radial-gradient(circle_at_10%_80%,rgba(82,12,35,.30),transparent_30%)]" />
          <div class="absolute right-[-10%] top-[15%] -z-10 size-[560px] rounded-full border border-white/[.05]" />
          <div class="mx-auto grid min-h-[calc(100vh-5rem)] max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-[1.05fr_.95fr]">
            <div>
              <p class="mb-5 text-xs font-semibold uppercase tracking-[.32em] text-[#d26a8a]">{{ publishedContent.heroEyebrow }}</p>
              <h1 class="max-w-3xl font-display text-5xl leading-[1.04] sm:text-6xl lg:text-7xl">{{ publishedContent.headline }}</h1>
              <p class="mt-7 max-w-xl text-base leading-7 text-white/60 sm:text-lg">{{ publishedContent.introduction }}</p>
              <div class="mt-9 flex flex-wrap gap-3">
                <button class="primary-button" type="button" @click="chat.open('Vamos consultar os horários disponíveis. Para qual serviço você deseja atendimento?')">{{ publishedContent.heroPrimaryCta }}</button>
                <a class="secondary-button" :href="whatsappUrl" target="_blank" rel="noreferrer">{{ publishedContent.heroSecondaryCta }}</a>
              </div>
            </div>
            <div class="relative mx-auto aspect-[4/5] w-full max-w-md overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-[#35101f] via-[#170a11] to-black shadow-[0_40px_120px_rgba(90,9,37,.35)]">
              <img v-if="heroImage?.publicUrl" :src="heroImage.publicUrl" :alt="heroImage.altText ?? ''" class="absolute inset-0 size-full object-cover" />
              <div v-else class="absolute inset-0 grid place-items-center text-center">
                <div>
                  <div class="mx-auto grid size-24 place-items-center rounded-full border border-white/10 bg-white/[.04] text-5xl">✦</div>
                  <p class="mt-6 font-display text-3xl">{{ publishedContent.displayName }}</p>
                  <p class="mt-2 text-sm text-white/40">{{ publishedContent.heroImagePlaceholder }}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="sobre" class="border-y border-white/[.06] bg-white/[.015] py-24">
          <div class="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[.7fr_1.3fr]">
            <p class="text-xs font-semibold uppercase tracking-[.30em] text-[#d26a8a]">{{ publishedContent.aboutLabel }}</p>
            <div>
              <h2 class="font-display text-4xl sm:text-5xl">{{ publishedContent.aboutTitle }}</h2>
              <p class="mt-6 max-w-3xl whitespace-pre-wrap leading-8 text-white/58">{{ publishedContent.biography }}</p>
            </div>
          </div>
        </section>

        <section id="servicos" class="py-24">
          <div class="mx-auto max-w-7xl px-6">
            <div class="flex flex-wrap items-end justify-between gap-6">
              <div><p class="section-label">{{ publishedContent.servicesLabel }}</p><h2 class="section-title">{{ publishedContent.servicesTitle }}</h2></div>
              <div class="flex flex-wrap gap-3"><button class="secondary-button" type="button" @click="managerOpen = true">{{ publishedContent.servicesManageCta }}</button><button class="secondary-button" type="button" @click="chat.open('Posso explicar cada serviço e consultar a agenda.')">{{ publishedContent.servicesChatCta }}</button></div>
            </div>

            <div v-if="services.length" class="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              <article v-for="service in services" :key="service.id" class="glass-card">
                <p class="text-xs uppercase tracking-[.22em] text-[#d26a8a]">{{ service.durationMinutes }} minutos</p>
                <h3 class="mt-4 font-display text-3xl">{{ service.name }}</h3>
                <div class="service-description mt-4 min-h-16 text-sm leading-6 text-white/55">
                  <template v-for="(block, blockIndex) in formatPublicDescription(service.publicDescription)" :key="`${block.type}-${blockIndex}`">
                    <p v-if="block.type === 'paragraph'">{{ block.text }}</p>
                    <component :is="block.ordered ? 'ol' : 'ul'" v-else>
                      <li v-for="(item, itemIndex) in block.items" :key="itemIndex">{{ item }}</li>
                    </component>
                  </template>
                </div>
                <div class="mt-7 flex items-center justify-between border-t border-white/10 pt-5">
                  <strong class="text-lg">{{ service.priceMode === 'ON_REQUEST' ? service.priceLabel : formatPrice(service.priceCents) }}</strong>
                  <a v-if="service.priceMode === 'ON_REQUEST'" class="text-sm text-[#e2839f]" :href="serviceWhatsappUrl(service)" target="_blank" rel="noreferrer">Falar no WhatsApp →</a>
                  <button v-else class="text-sm text-[#e2839f]" type="button" @click="openBooking(service)">{{ publishedContent.serviceCardCta }} →</button>
                </div>
              </article>
            </div>

            <div v-else class="mt-12 rounded-[2rem] border border-white/10 bg-white/[.025] p-8 text-center">
              <p class="text-white/65">
                {{ servicesUnavailable ? publishedContent.servicesUnavailableText : publishedContent.servicesEmptyText }}
              </p>
              <div class="mt-6 flex flex-wrap justify-center gap-3">
                <button class="primary-button" type="button" @click="chat.open('Gostaria de receber informações sobre os serviços.')">{{ publishedContent.servicesEmptyChatCta }}</button>
                <a class="secondary-button" :href="whatsappUrl" target="_blank" rel="noreferrer">{{ publishedContent.servicesEmptyWhatsappCta }}</a>
              </div>
            </div>
          </div>
        </section>

        <section id="galeria" class="border-y border-white/[.06] bg-[#10070b] py-24">
          <div class="mx-auto max-w-7xl px-6">
            <p class="section-label">{{ publishedContent.galleryLabel }}</p><h2 class="section-title">{{ publishedContent.galleryTitle }}</h2>
            <div v-if="publishedMedia.length" class="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
              <article v-for="asset in publishedMedia" :key="asset.id" class="aspect-[4/5] overflow-hidden rounded-[1.5rem] border border-white/10 bg-black/30">
                <img v-if="asset.mediaType === 'IMAGE'" :src="asset.publicUrl ?? ''" :alt="asset.altText ?? ''" class="size-full object-cover" loading="lazy" />
                <video v-else :src="asset.publicUrl ?? ''" class="size-full object-cover" controls preload="metadata" />
              </article>
            </div>
            <div v-else class="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
              <div v-for="index in 4" :key="index" class="aspect-[4/5] rounded-[1.5rem] border border-white/10 bg-gradient-to-br from-[#32101e] to-[#0e070a]" />
            </div>
          </div>
        </section>

        <section id="duvidas" class="py-24">
          <div class="mx-auto max-w-4xl px-6">
            <p class="section-label text-center">{{ publishedContent.faqLabel }}</p><h2 class="section-title text-center">{{ publishedContent.faqTitle }}</h2>
            <div class="mt-12 space-y-4">
              <details v-for="item in [...publishedContent.faqItems].sort((a, b) => a.displayOrder - b.displayOrder)" :key="item.id" class="rounded-2xl border border-white/10 bg-white/[.025] p-5">
                <summary class="cursor-pointer font-medium">{{ item.question }}</summary>
                <p class="mt-4 text-sm leading-6 text-white/55">{{ item.answer }}</p>
              </details>
            </div>
          </div>

        <section v-if="visibleContacts.length" id="contatos" class="border-t border-white/[.06] bg-white/[.015] py-20">
          <div class="mx-auto max-w-5xl px-6">
            <p class="section-label text-center">{{ publishedContent.contactsLabel }}</p>
            <h2 class="section-title text-center">{{ publishedContent.contactsTitle }}</h2>
            <div class="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <article v-for="contact in visibleContacts" :key="contact.id" class="glass-card">
                <div class="flex items-start gap-4">
                  <span class="contact-card-icon">
                    <ContactIcon :type="contact.type" />
                  </span>
                  <div class="min-w-0">
                    <p class="text-xs uppercase tracking-[.18em] text-[#d26a8a]">{{ contact.label }}</p>
                    <a v-if="contactHref(contact.type, contact.value)" class="mt-3 block break-words text-white/75 hover:text-white" :href="contactHref(contact.type, contact.value) ?? '#'" target="_blank" rel="noreferrer">{{ contact.value }}</a>
                    <p v-else class="mt-3 break-words text-white/75">{{ contact.value }}</p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>
        </section>
      </main>

      <footer id="responsabilidade" class="border-t border-white/[.06] py-10">
        <div class="mx-auto max-w-5xl px-6">
          <div class="responsibility-notice">
            <span class="contact-card-icon shrink-0">
              <ContactIcon type="PRIVACY" />
            </span>
            <div>
              <strong class="text-sm text-white/80">Responsabilidade pelo conteúdo</strong>
              <p class="mt-2 text-sm leading-6 text-white/50">Todas as informações deste site são de total responsabilidade da Terapeuta. Nosso site apenas hospeda a página; o conteúdo é 100% gerenciado por ela.</p>
            </div>
          </div>
          <p class="mt-7 text-center text-sm text-white/35">{{ publishedContent.displayName }} · {{ publishedContent.publicLocation }} · Terapeut-IA®</p>
        </div>
      </footer>
      <ChatWidget :whatsapp-url="whatsappUrl" />
      <BookingModal v-if="selectedService" :service="selectedService" @close="selectedService = null" @reserved="handleReserved" />
      <AppointmentManagerModal v-if="managerOpen" @close="managerOpen = false" />
    </div>
  </AgeGate>
</template>
